import { useCallback, useEffect, useState } from 'react';
import { PageHeader, EmptyState, TableSkeleton } from '../components/common/PageHeader';
import { SearchFilterBar } from '../components/common/SearchFilterBar';
import { Modal } from '../components/common/Modal';
import { ActiveBadge } from '../components/common/StatusBadge';
import ConsultantForm, {
  emptyConsultantForm,
  formToConsultantFormData,
  consultantToForm,
} from '../components/forms/ConsultantForm';
import { listConsultants, createConsultant, updateConsultant } from '../services/consultantService';
import { getErrorMessage } from '../services/apiClient';
import { useToast } from '../context/ToastContext';

export default function ConsultantsPage() {
  const { showToast } = useToast();
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyConsultantForm);

  const fetchConsultants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listConsultants();
      setConsultants(res.data || []);
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchConsultants(); }, [fetchConsultants]);

  const filtered = consultants.filter((c) => {
    const matchSearch = !search.trim() ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.specialization?.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && c.active !== false) ||
      (statusFilter === 'inactive' && c.active === false);
    return matchSearch && matchStatus;
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyConsultantForm);
    setModalOpen(true);
  };

  const openEdit = (consultant) => {
    setEditing(consultant);
    setForm(consultantToForm(consultant));
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = formToConsultantFormData(form);
      if (editing) {
        await updateConsultant(editing._id, payload);
        showToast('Consultant updated successfully');
      } else {
        await createConsultant(payload);
        showToast('Consultant created successfully');
      }
      setModalOpen(false);
      fetchConsultants();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Consultants"
        description="Manage wellness consultants"
        action={<button type="button" className="btn btn-primary" onClick={openCreate}>+ Add Consultant</button>}
      />

      <div className="card">
        <SearchFilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search consultants..."
          filters={[{
            key: 'status',
            label: 'Status',
            options: [
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ],
          }]}
          filterValues={{ status: statusFilter }}
          onFilterChange={(_, value) => setStatusFilter(value)}
          onClear={() => { setSearch(''); setStatusFilter('all'); }}
        />

        {loading ? (
          <TableSkeleton cols={5} />
        ) : filtered.length === 0 ? (
          <EmptyState title="No consultants found" action={{ label: 'Add Consultant', onClick: openCreate }} />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Consultant</th>
                  <th>Specialization</th>
                  <th>Experience</th>
                  <th>Rating</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c._id}>
                    <td>
                      <div className="cell-product">
                        {c.image && <img src={c.image} alt="" className="cell-thumb round" />}
                        <div>
                          <p className="cell-title">{c.name}</p>
                          <p className="cell-sub">{c.qualifications}</p>
                        </div>
                      </div>
                    </td>
                    <td>{c.specialization || '—'}</td>
                    <td>{c.experience || '—'}</td>
                    <td>{c.rating ? `${c.rating} ★` : '—'}</td>
                    <td><ActiveBadge active={c.active !== false} /></td>
                    <td>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(c)}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Consultant' : 'Add Consultant'}
        size="lg"
      >
        <form onSubmit={handleSave}>
          <ConsultantForm form={form} onChange={setForm} />
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
